import React, { createContext, useContext, useState, useCallback } from 'react';

export type MessageChannel = 'sms' | 'email' | 'note';

/** A request to focus the feed's message input (optionally on a given channel /
 *  recipient). The `nonce` makes each request distinct so the input re-acts even
 *  when the same channel/recipient is requested twice in a row. */
export interface MessageFocusRequest {
  channel?: MessageChannel;
  /** Match the recipient picker entry by phone number (preferred — works for
   *  both funeral clients and lead contacts). */
  recipientPhone?: string | null;
  nonce: number;
}

interface MessageDraftContextValue {
  draftMessage: string;
  setDraftMessage: (message: string) => void;
  clearDraft: () => void;
  focusRequest: MessageFocusRequest | null;
  /** Ask the feed's MessageInputBar to focus (and optionally switch channel /
   *  select a recipient). Used by the contact-card SMS quick action. */
  requestFocus: (req: Omit<MessageFocusRequest, 'nonce'>) => void;
}

const MessageDraftContext = createContext<MessageDraftContextValue | null>(null);

export function MessageDraftProvider({ children }: { children: React.ReactNode }) {
  const [draftMessage, setDraftMessage] = useState('');
  const [focusRequest, setFocusRequest] = useState<MessageFocusRequest | null>(null);
  const clearDraft = useCallback(() => setDraftMessage(''), []);
  const requestFocus = useCallback((req: Omit<MessageFocusRequest, 'nonce'>) => {
    setFocusRequest({ ...req, nonce: Date.now() });
  }, []);

  return (
    <MessageDraftContext.Provider
      value={{ draftMessage, setDraftMessage, clearDraft, focusRequest, requestFocus }}
    >
      {children}
    </MessageDraftContext.Provider>
  );
}

const noopDraft: MessageDraftContextValue = {
  draftMessage: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setDraftMessage: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearDraft: () => {},
  focusRequest: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  requestFocus: () => {},
};

export function useMessageDraft() {
  const context = useContext(MessageDraftContext);
  return context ?? noopDraft;
}
